import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowUpRight, BookOpen } from "lucide-react";
import SEO from "@/components/SEO";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import EduSearch from "@/components/education/EduSearch";
import { ArticleCard } from "@/components/education/ArticleCard";
import {
  searchAll,
  SEARCH_TYPES,
  rankArticles,
  articlePlainText,
  getArticle,
} from "@/data/education";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AiAnswer({ query }) {
  const [state, setState] = useState({ loading: true, answer: "", sources: [], grounded: true, error: false });
  const cache = useRef({});

  useEffect(() => {
    const q = (query || "").trim();
    if (!q) return;

    if (cache.current[q]) {
      setState({ ...cache.current[q], loading: false });
      return;
    }

    const candidates = rankArticles(q, 6);
    setState({ loading: true, answer: "", sources: [], grounded: true, error: false });

    if (candidates.length === 0) {
      const result = {
        answer:
          "I couldn't find an Education Center article that matches your question yet. Try different keywords, browse the categories below, or reach out to an Innervation advisor who can help directly.",
        sources: [],
        grounded: false,
        error: false,
      };
      cache.current[q] = result;
      setState({ ...result, loading: false });
      return;
    }

    const payload = {
      question: q,
      articles: candidates.map((a) => ({
        slug: a.slug,
        title: a.title,
        category: a.categoryLabel,
        content: articlePlainText(a),
      })),
    };

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/education/ask`, payload, { timeout: 45000 });
        const result = {
          answer: data.answer || "",
          sources: data.sources || [],
          grounded: data.grounded !== false,
          error: false,
        };
        cache.current[q] = result;
        if (!cancelled) setState({ ...result, loading: false });
      } catch (e) {
        if (!cancelled)
          setState({
            loading: false,
            answer:
              "Our AI assistant is taking a break right now. The matching articles below should still point you in the right direction.",
            sources: candidates.slice(0, 3).map((a) => a.slug),
            grounded: false,
            error: true,
          });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const cited = useMemo(
    () => (state.sources || []).map(getArticle).filter(Boolean).slice(0, 3),
    [state.sources]
  );

  return (
    <div
      data-testid="ai-answer-panel"
      className="relative overflow-hidden border border-line bg-white"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-electric/10 blur-[90px]" />
      <div className="relative p-7 md:p-10">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-electric text-white">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-electric">
              AI Answer
            </p>
            <p className="text-xs text-slatey">Synthesized from the IEG Education Center</p>
          </div>
        </div>

        {state.loading ? (
          <div className="mt-7" data-testid="ai-answer-loading">
            <div className="flex items-center gap-2 text-sm text-slatey">
              <Loader2 className="h-4 w-4 animate-spin text-electric" />
              Reading the library and composing an answer…
            </div>
            <div className="mt-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-3.5 animate-pulse rounded-sm bg-line"
                  style={{ width: `${[92, 100, 68][i]}%` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="ai-answer-text"
              className="mt-6 text-lg leading-relaxed text-navy/85"
            >
              {state.answer}
            </motion.p>

            {cited.length > 0 && (
              <div className="mt-8">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                  <BookOpen className="h-4 w-4 text-electric" />
                  Where to read more
                </p>
                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-3">
                  {cited.map((a) => (
                    <ArticleCard key={a.slug} article={a} testid={`ai-source-${a.slug}`} />
                  ))}
                </div>
              </div>
            )}

            <p className="mt-7 border-t border-line pt-4 text-xs text-slatey">
              AI-generated from our published articles. Always verify specifics with an advisor before
              making procurement decisions.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function EduSearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const type = params.get("type") || "all";

  const results = useMemo(() => searchAll(q, type), [q, type]);

  const setType = (t) => {
    const next = new URLSearchParams(params);
    if (t === "all") next.delete("type");
    else next.set("type", t);
    setParams(next, { replace: true });
  };

  return (
    <>
      <SEO
        title={q ? `Search: ${q}` : "Search"}
        description="Search the Innervation Energy Group Education Center — articles, learning paths, glossary terms, and state & industry guides — with an AI assistant that answers your commercial energy questions."
        path="/education/search"
      />

      {/* Hero + search */}
      <section className="relative overflow-hidden bg-navy pt-[74px] text-white md:pt-[88px]">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-50" />
        <div className="pointer-events-none absolute -right-32 top-10 h-[400px] w-[400px] rounded-full bg-electric/15 blur-[140px]" />
        <Container className="relative py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-energy">
            Education Center · Search
          </p>
          <h1 className="mt-4 font-heading text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            {q ? (
              <>
                Results for <span className="text-electric">“{q}”</span>
              </>
            ) : (
              "Search the knowledge library."
            )}
          </h1>
          <div className="mt-8 max-w-2xl">
            <EduSearch defaultValue={q} testid="results-search" />
          </div>
        </Container>
      </section>

      {/* AI answer */}
      {q && (
        <section className="bg-ice py-16 md:py-20">
          <Container>
            <Reveal>
              <AiAnswer query={q} />
            </Reveal>
          </Container>
        </section>
      )}

      {/* Results */}
      <section className="bg-white py-16 md:py-20">
        <Container>
          {/* Type filters */}
          <div className="flex flex-wrap gap-2" data-testid="search-type-filters">
            {SEARCH_TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                data-testid={`filter-${t.key}`}
                className={
                  "border px-4 py-2 text-sm font-medium transition-colors " +
                  (type === t.key
                    ? "border-electric bg-electric text-white"
                    : "border-line text-navy hover:border-electric")
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          <p className="mt-8 text-sm text-slatey" data-testid="results-count">
            {results.length} result{results.length !== 1 ? "s" : ""}
            {q ? "" : " — enter a search above to begin"}
          </p>

          {results.length > 0 ? (
            <div className="mt-6 divide-y divide-line border-t border-line" data-testid="search-results">
              {results.map((r, i) => (
                <Reveal key={`${r.path}-${i}`} delay={Math.min(i, 6) * 0.03}>
                  <Link
                    to={r.path}
                    data-testid={`result-${i}`}
                    className="group flex items-start justify-between gap-6 py-6"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-electric">
                        {r.tag}
                      </span>
                      <h3 className="mt-1.5 font-heading text-lg font-medium text-navy transition-colors group-hover:text-electric">
                        {r.title}
                      </h3>
                      {r.subtitle && (
                        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-slatey">
                          {r.subtitle}
                        </p>
                      )}
                    </div>
                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-slatey transition-colors group-hover:text-electric" />
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            q && (
              <div className="mt-10 border border-dashed border-line p-10 text-center" data-testid="no-results">
                <p className="text-navy">
                  No direct matches for “{q}”.
                </p>
                <p className="mt-2 text-sm text-slatey">
                  Try broader keywords, or{" "}
                  <Link to="/education" className="font-medium text-electric hover:underline">
                    browse the Education Center
                  </Link>
                  .
                </p>
              </div>
            )
          )}
        </Container>
      </section>

      <CTABand
        heading="Still have a question?"
        text="Our advisors can apply these concepts to your specific facilities, contracts, and markets — with independent, transparent guidance."
        secondary={{ label: "Back to Education Center", to: "/education" }}
      />
    </>
  );
}
